import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronDown, ChevronRight, ExternalLink, Loader2, RefreshCw, Search, Tags, Plus, EyeOff, Undo2, Link2 } from "lucide-react";
import { tarifasApi, errMsg } from "./api";

const NONE = "__none__";

export const AliasSuggestions = ({ providers, onChanged, onCreateProvider }) => {
  const [open, setOpen] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showIgnored, setShowIgnored] = useState(false);
  const [showAssigned, setShowAssigned] = useState(false);
  const [q, setQ] = useState("");
  const [limit, setLimit] = useState(30);
  const [picks, setPicks] = useState({});
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await tarifasApi.aliasSuggestions(showAssigned));
    } catch (e) {
      toast.error(errMsg(e, "No se pudieron analizar las marcas de KimKim"));
    } finally {
      setLoading(false);
    }
  }, [showAssigned]);

  // Carga inicial y recarga cuando cambian los proveedores o sus alias (alta/edición/borrado desde el listado)
  const providersKey = providers.map((p) => `${p.id}:${(p.aliases || []).join("|")}:${p.country}`).join(",");
  useEffect(() => { load(); }, [providersKey, load]);

  const provName = (id) => providers.find((p) => p.id === id)?.name || "";
  const norm = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const marks = (data?.marks || [])
    .filter((m) => (showIgnored || !m.ignored) && (!q || norm(m.mark).includes(norm(q)) || m.variants.some((v) => norm(v).includes(norm(q))) || m.locations.some((l) => norm(l).includes(norm(q)))));
  const pending = (data?.marks || []).filter((m) => !m.ignored && !m.assigned_provider_id).length;
  const pickOf = (m) => picks[m.key] ?? m.suggested_provider_id ?? NONE;

  const assign = async (m) => {
    const pid = pickOf(m);
    if (pid === NONE) return toast.error("Elige el proveedor al que pertenece esta marca");
    setBusy(m.key);
    try {
      const p = await tarifasApi.aliasAssign(pid, m.mark, m.country_hint);
      if (p.kimkim_count > 0) toast.success(`«${m.mark}» añadido como alias de ${p.name} · ${p.kimkim_count} fichas de KimKim enlazadas`);
      else toast.warning(`«${m.mark}» añadido a ${p.name}, pero no se enlazó ninguna ficha: revisa el país / catálogo del proveedor`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(errMsg(e, "No se pudo añadir el alias"));
    } finally {
      setBusy("");
    }
  };

  const ignore = async (m) => {
    setBusy(m.key);
    try {
      if (m.ignored) await tarifasApi.aliasUnignore(m.key);
      else await tarifasApi.aliasIgnore(m.mark);
      await load();
    } catch (e) {
      toast.error(errMsg(e, "No se pudo actualizar"));
    } finally {
      setBusy("");
    }
  };

  return (
    <Card className="rounded-md mb-3" data-testid="alias-suggestions">
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <button className="flex items-center gap-2 text-left" onClick={() => setOpen((o) => !o)} data-testid="alias-suggestions-toggle">
            {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            <Tags className="h-4 w-4" />
            <h3 className="font-heading text-base font-bold">Marcas encontradas en KimKim sin proveedor</h3>
            {data && <Badge className="rounded-md bg-amber-100 text-amber-800 hover:bg-amber-100" data-testid="alias-pending-count">{pending}</Badge>}
          </button>
          <div className="flex items-center gap-2">
            <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={showAssigned} onChange={(e) => setShowAssigned(e.target.checked)} data-testid="alias-show-assigned" /> ver ya asignadas</label>
            <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={showIgnored} onChange={(e) => setShowIgnored(e.target.checked)} data-testid="alias-show-ignored" /> ver ignoradas</label>
            <Button data-testid="alias-refresh" size="sm" variant="outline" className="rounded-md" onClick={load} disabled={loading}>{loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}</Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">Se analizan las notas de precio («2026 // UA», «GP // Net price…») y el campo operador de las {data?.total_activities ?? "…"} fichas del catálogo sincronizado. Elige el proveedor y pulsa "Añadir alias": las fichas con esa marca quedan enlazadas al proveedor.</p>
      </CardHeader>
      {open && (
        <CardContent className="pt-0">
          <div className="flex items-center gap-2 pb-2">
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2 top-2.5 text-muted-foreground" />
              <Input data-testid="alias-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar marca o destino…" className="h-8 w-56 pl-7 rounded-md text-xs" />
            </div>
            <span className="text-xs text-muted-foreground" data-testid="alias-count">{marks.length} marcas</span>
          </div>
          {!data && <p className="text-sm text-muted-foreground py-3">Analizando…</p>}
          {data && marks.length === 0 && <p className="text-sm text-muted-foreground py-3" data-testid="alias-empty">No quedan marcas sin proveedor.</p>}
          {marks.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="alias-table">
                <thead className="text-muted-foreground">
                  <tr className="text-left">
                    <th className="py-1 pr-3 font-medium">Marca en KimKim</th>
                    <th className="pr-3 font-medium">Fichas</th>
                    <th className="pr-3 font-medium">Destinos</th>
                    <th className="pr-3 font-medium">Ejemplo</th>
                    <th className="pr-3 font-medium">Proveedor</th>
                    <th className="font-medium">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {marks.slice(0, limit).map((m) => {
                    const s = m.samples[0];
                    const pick = pickOf(m);
                    return (
                      <tr key={m.key} className={`border-t align-top ${m.ignored ? "opacity-50" : ""}`} data-testid={`alias-row-${m.key}`}>
                        <td className="py-2 pr-3">
                          <span className="font-mono font-semibold text-sm">{m.mark}</span>
                          {m.variants.length > 1 && <div className="text-muted-foreground">también: {m.variants.slice(1, 4).join(", ")}</div>}
                          {m.assigned_provider_id && <Badge className="rounded-md bg-emerald-100 text-emerald-800 hover:bg-emerald-100 mt-1">ya es alias de {m.assigned_provider_name}</Badge>}
                          {m.ignored && <Badge className="rounded-md bg-slate-100 text-slate-600 hover:bg-slate-100 mt-1">ignorada</Badge>}
                        </td>
                        <td className="py-2 pr-3 whitespace-nowrap"><strong>{m.count}</strong> <span className="text-muted-foreground">({m.from_operator ? `${m.from_operator} operador` : ""}{m.from_operator && m.from_notes ? ", " : ""}{m.from_notes ? `${m.from_notes} notas` : ""})</span></td>
                        <td className="py-2 pr-3 max-w-[12rem]">{m.locations.join(" · ") || "—"}</td>
                        <td className="py-2 pr-3 max-w-xs">
                          {s && (
                            <div>
                              <a href={s.url} target="_blank" rel="noreferrer" className="underline flex items-center gap-0.5">{s.title} <ExternalLink className="h-3 w-3" /></a>
                              {s.note && <div className="text-muted-foreground line-clamp-2" title={s.note}>«{s.note}»</div>}
                            </div>
                          )}
                        </td>
                        <td className="py-2 pr-3">
                          {!m.assigned_provider_id && (
                            <Select value={pick} onValueChange={(v) => setPicks({ ...picks, [m.key]: v })}>
                              <SelectTrigger data-testid={`alias-pick-${m.key}`} className={`h-8 w-56 rounded-md text-xs ${m.suggested_provider_id && picks[m.key] === undefined ? "border-sky-400" : ""}`}><SelectValue /></SelectTrigger>
                              <SelectContent className="max-h-72">
                                <SelectItem value={NONE}>— elegir proveedor —</SelectItem>
                                {providers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}{p.destination ? ` · ${p.destination}` : ""}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          )}
                          {m.suggested_provider_id && picks[m.key] === undefined && !m.assigned_provider_id && <div className="text-[11px] text-sky-700 mt-0.5">sugerido: {provName(m.suggested_provider_id)}</div>}
                        </td>
                        <td className="py-2">
                          <div className="flex flex-wrap gap-1">
                            {!m.assigned_provider_id && (
                              <Button data-testid={`alias-assign-${m.key}`} size="sm" className="rounded-md h-7" disabled={busy === m.key || pick === NONE} onClick={() => assign(m)}><Link2 className="h-3 w-3 mr-1" /> Añadir alias</Button>
                            )}
                            {!m.assigned_provider_id && (
                              <Button data-testid={`alias-new-provider-${m.key}`} size="sm" variant="outline" className="rounded-md h-7" onClick={() => onCreateProvider?.({ aliases: m.mark, country: m.country_hint || "", destination: m.locations[0] || "", notes: `Marca «${m.mark}» encontrada en ${m.count} fichas de KimKim` })}><Plus className="h-3 w-3 mr-1" /> Nuevo proveedor</Button>
                            )}
                            <Button data-testid={`alias-ignore-${m.key}`} size="sm" variant="ghost" className="rounded-md h-7" disabled={busy === m.key} onClick={() => ignore(m)}>{m.ignored ? <><Undo2 className="h-3 w-3 mr-1" /> Recuperar</> : <><EyeOff className="h-3 w-3 mr-1" /> Ignorar</>}</Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {marks.length > limit && <Button data-testid="alias-show-more" size="sm" variant="ghost" className="rounded-md mt-2" onClick={() => setLimit((l) => l + 30)}>Mostrar {Math.min(30, marks.length - limit)} más ({marks.length - limit} restantes)</Button>}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};
